import React from 'react';
import {
    Autocomplete,
    Avatar,
    Box,
    Grid,
    Typography,
    TextField,
    Button,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Chip,
    IconButton,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import type { MenuProps } from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';
import { UploadFile, PictureAsPdf, Delete } from '@mui/icons-material';
import { theme } from '../../../../constants/theme';
import { FONT_ENUM } from '../../../../types/global/fonts.types';
import { CustomDatePicker } from '../../../../components/DatePicker/CustomDatePicker';
import { InfoTextField } from '../../../../components/Common/InfoTextField';
import { PlacesAutocompleteField, type PlacesSuggestionPalette } from '../../../Common/PlacesAutocompleteField';
import { ILocation, ICastingDirector } from '../../../../types/global/helper';
import { currencySymbols } from '../../../../constants/currency';
import { RichTextEditor } from '../../../Common/RichTextEditor';
import { useSelector } from 'react-redux';
import { brandsSelector } from '../../../../redux/buzzbridge/selectors';
import { normalizeCastingDirectorRecord } from '../../../../utils/castingDirectorNormalize';
import { CastingDirectorCard } from '../../../SearchAdd/CastingDirectorCard';
import {
    AUDITION_PLOT_PLACEHOLDER,
    getAuditionSynopsisLabel,
    getAuditionSynopsisPlaceholder,
} from '../../../../utils/auditionRichTextCopy';
import {
    BRAND_LUXE_PRIMARY,
    BRAND_MODAL_ACCENT_CHIP_BG,
    BRAND_OUTLINE_VARIANT,
    BRAND_SURFACE_CONTAINER_HIGH,
} from '../../../../pages/BrandDashboard/brandDashboardTokens';
import { useDispatch } from 'react-redux';
import { setSystemForm } from '../../../../redux/appConfigurator/action';
import { FORM_IDENTIFIER_ENUM } from '../../../../types/global/helper';

const availableTags = [
    'paid',
    'unpaid',
    'nollywood',
    'short film',
    'feature',
    'photo shoot',
    'event',
    'student film',
    'ghallywood',
    'hollywood',
    'music video',
];

/** Menu opens below the field in scrollable dialogs; avoids scroll-lock fighting the dialog. */
function auditionEditSelectMenuProps(paperSx: Record<string, unknown>): Partial<MenuProps> {
    return {
        disableScrollLock: true,
        anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
        transformOrigin: { vertical: 'top', horizontal: 'left' },
        PaperProps: {
            sx: {
                maxHeight: 288,
                mt: 0.5,
                ...paperSx,
            },
        },
    };
}

export interface AuditionDetailsEditFormBodyProps {
    themeStyle: keyof typeof theme;
    values: Record<string, any>;
    onFieldChange: (field: string, value: any) => void;
    isMapsLoaded: boolean;
    visualVariant?: 'dashboard' | 'brand';
    onAddProductionCompany?: () => void;
}

export const AuditionDetailsEditFormBody: React.FC<AuditionDetailsEditFormBodyProps> = ({
    themeStyle,
    values,
    onFieldChange,
    isMapsLoaded,
    visualVariant = 'dashboard',
    onAddProductionCompany,
}) => {
    const dispatch = useDispatch();
    const userBrands = useSelector(brandsSelector);
    const castingDirectors: ICastingDirector[] = userBrands
        .map(b => {
            const companyId: string | undefined =
                typeof b.company === 'string' ? b.company : (b.company as any)?._id ?? (b.company as any)?.id;
            if (!companyId) return null;
            return normalizeCastingDirectorRecord({ ...b, _id: companyId, company_name: b.name, profile_picture: b.logo ?? '' });
        })
        .filter(Boolean) as ICastingDirector[];
    const fs = theme[themeStyle as keyof typeof theme];
    const isBrand = visualVariant === 'brand';
    const fieldBg = isBrand ? BRAND_SURFACE_CONTAINER_HIGH : fs.gray_text;
    const sectionTitle = fs.primary_white;
    const labelMuted = isBrand ? fs.primary_white : fs.light_gray_text;
    const outlineLabel = isBrand ? 'rgba(255,255,255,0.86)' : fs.tag_white;
    const accent = isBrand ? BRAND_LUXE_PRIMARY : fs.primary_orange;
    const menuSelFg = isBrand ? '#0a1211' : fs.black;
    const chipTint = isBrand ? BRAND_MODAL_ACCENT_CHIP_BG : fs.background_gradient.end;
    const bodyText = isBrand ? 'rgba(255,255,255,0.9)' : fs.primary_white;
    const synopsisLabel = getAuditionSynopsisLabel(values.gig_type);
    const synopsisPlaceholder = getAuditionSynopsisPlaceholder(values.gig_type);
    const richTextShell = isBrand ? 'brandDashboard' : 'default';
    /** Same language as union Select menu: dark paper, accent + dark fg on active row. */
    const locationSuggestionPalette: PlacesSuggestionPalette = {
        rowInactiveBg: fieldBg,
        rowInactiveColor: bodyText,
        rowActiveBg: accent,
        rowActiveColor: menuSelFg,
        loadingBg: fieldBg,
        loadingColor: bodyText,
        listBorder: isBrand ? `1px solid ${BRAND_OUTLINE_VARIANT}` : '1px solid rgba(255,255,255,0.12)',
    };
    const emptyLocation = (): ILocation => ({
        type: 'Point',
        coordinates: [0, 0],
        address: '',
        description: '',
        short_name: '',
    });
    const locationAddressFromValues = (loc: unknown): string => {
        if (Array.isArray(loc)) {
            return (loc[0] as ILocation | undefined)?.address || '';
        }
        return (loc as ILocation | undefined)?.address || '';
    };
    const mergeLocationAddress = (loc: unknown, address: string): ILocation => {
        const base: ILocation =
            Array.isArray(loc) && loc.length > 0
                ? (loc[0] as ILocation)
                : !Array.isArray(loc) && loc && typeof loc === 'object'
                ? (loc as ILocation)
                : emptyLocation();
        return { ...base, address };
    };
    const selectMenuPaperSx: Record<string, unknown> = {
        backgroundColor: fieldBg,
        color: bodyText,
        '& .MuiMenuItem-root': {
            color: bodyText,
            '&:hover': {
                backgroundColor: chipTint,
            },
            '&.Mui-selected': {
                backgroundColor: accent,
                color: menuSelFg,
                '&:hover': {
                    backgroundColor: accent,
                },
            },
        },
    };
    const titleSx = {
        fontFamily: FONT_ENUM.MAIN_BOLD,
        fontSize: 11,
        color: sectionTitle,
        textTransform: 'uppercase' as const,
        letterSpacing: '0.14em',
    };
    const labelSx = {
        fontFamily: FONT_ENUM.MAIN_BOLD,
        color: labelMuted,
        mb: 1.5,
        textTransform: 'uppercase' as const,
        letterSpacing: '0.1em',
        fontSize: 11,
    };
    const sectionHeader = (label: string) => <Typography sx={{ ...titleSx, mb: 2 }}>{label}</Typography>;
    const locationFieldSx: SxProps<Theme> = isBrand
        ? {
              '& .MuiOutlinedInput-root': {
                  bgcolor: fieldBg,
                  '& fieldset': { border: 'none' },
                  '&:hover fieldset': { border: 'none' },
                  '&.Mui-focused fieldset': { border: 'none' },
                  '&.Mui-focused': { boxShadow: '0 0 0 1px rgba(236,170,0,0.35)' },
              },
              '& .MuiOutlinedInput-input::placeholder': {
                  color: 'rgba(255,255,255,0.48)',
                  opacity: 1,
              },
          }
        : {};

    return (
        <Box
            sx={{
                pt: 1,
                ...(isBrand && {
                    '& .MuiInputLabel-root': {
                        color: 'rgba(255,255,255,0.65)',
                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                        fontSize: 15,
                        letterSpacing: '0.01em',
                    },
                    '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        backgroundColor: fieldBg,
                        color: fs.primary_white,
                        '& .MuiOutlinedInput-notchedOutline': {
                            border: 'none',
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                            border: 'none',
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            border: 'none',
                        },
                        '&.Mui-focused': {
                            boxShadow: '0 0 0 1px rgba(236,170,0,0.35)',
                        },
                        '& input, & textarea': {
                            color: fs.primary_white,
                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                            fontSize: 16,
                        },
                        '& input::placeholder, & textarea::placeholder': {
                            color: 'rgba(255,255,255,0.48)',
                            opacity: 1,
                        },
                    },
                    '& .MuiSelect-select': {
                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                        fontSize: 16,
                    },
                    '& .MuiButton-root': {
                        borderRadius: 999,
                    },
                }),
            }}>
            <Box sx={{ mb: 3 }}>
                {sectionHeader('Details')}
                <Grid container spacing={2} sx={{ mt: 1 }}>
                    <Grid item xs={12}>
                        <Typography sx={labelSx}>Title</Typography>
                        <TextField
                            fullWidth
                            placeholder="Audition Title"
                            value={values.title || ''}
                            onChange={e => onFieldChange('title', e.target.value)}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    color: outlineLabel,
                                    backgroundColor: fieldBg,
                                },
                            }}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <Typography sx={labelSx}>{synopsisLabel}</Typography>

                        <RichTextEditor
                            value={values.synopsis || ''}
                            onChange={value => onFieldChange('synopsis', value)}
                            shellVariant={richTextShell}
                            placeholder={synopsisPlaceholder}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <Typography sx={labelSx}>Additional Details / Plot</Typography>

                        <RichTextEditor
                            value={values.plot || ''}
                            onChange={value => onFieldChange('plot', value)}
                            shellVariant={richTextShell}
                            placeholder={AUDITION_PLOT_PLACEHOLDER}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <Typography sx={labelSx}>Company Website</Typography>
                        <TextField
                            fullWidth
                            placeholder="Company Website URL"
                            value={values.company_website || ''}
                            onChange={e => onFieldChange('company_website', e.target.value)}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    color: outlineLabel,
                                    backgroundColor: fieldBg,
                                },
                            }}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <Typography sx={labelSx}>Production Personnel</Typography>
                        <TextField
                            fullWidth
                            placeholder="Production Personnel"
                            value={values.production_personel || ''}
                            onChange={e => onFieldChange('production_personel', e.target.value)}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    color: outlineLabel,
                                    backgroundColor: fieldBg,
                                },
                            }}
                        />
                    </Grid>
                </Grid>
            </Box>

            <Box sx={{ mb: 3 }}>
                {sectionHeader('Add a production company or agency')}
                <Grid container spacing={2} sx={{ mt: 1 }}>
                    <Grid item xs={12}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Autocomplete
                                options={castingDirectors}
                                value={
                                    castingDirectors.find(
                                        cd => cd._id === (values.production_company?._id ?? values.production_company),
                                    ) ?? null
                                }
                                onChange={(_, value) =>
                                    onFieldChange(
                                        'production_company',
                                        value ? normalizeCastingDirectorRecord(value) : null,
                                    )
                                }
                                isOptionEqualToValue={(option, value) => !!value && option._id === value._id}
                                getOptionLabel={option => option.company_name || ''}
                                renderInput={params => (
                                    <TextField
                                        {...params}
                                        placeholder="Search or select..."
                                        variant="outlined"
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                bgcolor: fieldBg,
                                                borderRadius: 2,
                                                minHeight: 52,
                                                color: bodyText,
                                                '& fieldset': { border: 'none' },
                                                '&:hover fieldset': { border: 'none' },
                                                '&.Mui-focused fieldset': { border: 'none' },
                                                '&.Mui-focused': {
                                                    boxShadow: `0 0 0 1px ${accent}55`,
                                                },
                                            },
                                            '& .MuiInputBase-input': { color: bodyText },
                                            '& .MuiSvgIcon-root': { color: bodyText },
                                        }}
                                    />
                                )}
                                renderOption={(props, option) =>
                                    (() => {
                                        const { key, ...optionProps } = props;
                                        return (
                                            <Box
                                                component="li"
                                                key={option._id || String(key)}
                                                {...optionProps}
                                                sx={{
                                                    px: 2,
                                                    py: 1.5,
                                                    bgcolor: fieldBg,
                                                    '&:hover': { bgcolor: 'rgba(255,255,255,0.06)' },
                                                    '&.Mui-focused': { bgcolor: 'rgba(255,255,255,0.06)' },
                                                }}>
                                                <CastingDirectorCard castingDirector={option} mode="search" />
                                            </Box>
                                        );
                                    })()
                                }
                                slotProps={{
                                    paper: {
                                        sx: {
                                            bgcolor: fieldBg,
                                            backgroundImage: 'none',
                                            mt: 1,
                                            border: `1px solid ${BRAND_OUTLINE_VARIANT}`,
                                            borderRadius: 1.5,
                                            p: 1,
                                            boxShadow: '0 10px 26px rgba(0,0,0,0.45)',
                                            '& .MuiAutocomplete-listbox': { p: 0 },
                                        },
                                    },
                                }}
                                sx={{ flex: 1 }}
                            />
                            <IconButton
                                onClick={
                                    onAddProductionCompany ||
                                    (() => dispatch(setSystemForm(FORM_IDENTIFIER_ENUM.CREATE_PRODUCTION_COMPANY)))
                                }
                                sx={{
                                    width: 52,
                                    height: 52,
                                    borderRadius: 1.5,
                                    bgcolor: accent,
                                    color: menuSelFg,
                                    '&:hover': { opacity: 0.88, bgcolor: accent },
                                }}>
                                <AddIcon sx={{ fontSize: 34 }} />
                            </IconButton>
                        </Box>
                        {values.production_company && (
                            <Box mt={1}>
                                <Chip
                                    avatar={
                                        <Avatar
                                            src={values.production_company?.profile_picture || ''}
                                            alt={values.production_company?.company_name || ''}
                                            sx={{ width: 32, height: 32 }}
                                        />
                                    }
                                    label={values.production_company?.company_name || ''}
                                    onDelete={() => onFieldChange('production_company', null)}
                                    sx={{
                                        height: 52,
                                        px: 1,
                                        borderRadius: 999,
                                        bgcolor: fieldBg,
                                        border: `1px solid ${BRAND_OUTLINE_VARIANT}`,
                                        '& .MuiChip-label': { color: bodyText, fontFamily: FONT_ENUM.MAIN_MEDIUM, fontSize: 16, px: 1.25 },
                                        '& .MuiChip-deleteIcon': { color: '#101216', backgroundColor: '#ef4444', borderRadius: '50%', fontSize: 18, p: 0.15 },
                                    }}
                                />
                            </Box>
                        )}
                    </Grid>
                </Grid>
            </Box>

            <Box sx={{ mb: 3 }}>
                {sectionHeader('Tags')}
                <Grid container spacing={2} sx={{ mt: 1 }}>
                    <Grid item xs={12}>
                        <FormControl fullWidth>
                            <InputLabel id="aud-edit-tags-lbl" sx={{ color: outlineLabel }}>
                                Tags
                            </InputLabel>
                            <Select
                                labelId="aud-edit-tags-lbl"
                                id="aud-edit-tags-select"
                                multiple
                                value={values.tags || []}
                                onChange={e => onFieldChange('tags', e.target.value)}
                                label="Tags"
                                renderValue={selected => (
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                        {selected.map((value: any) => (
                                            <Chip
                                                key={value}
                                                label={value}
                                                size="small"
                                                sx={{
                                                    backgroundColor: chipTint,
                                                    color: bodyText,
                                                    fontFamily: FONT_ENUM.MAIN_REGULAR,
                                                    fontSize: '0.75rem',
                                                }}
                                            />
                                        ))}
                                    </Box>
                                )}
                                sx={{
                                    color: outlineLabel,
                                    backgroundColor: fieldBg,
                                    '& .MuiSelect-select': {
                                        backgroundColor: fieldBg,
                                    },
                                    '& .MuiSvgIcon-root': {
                                        color: bodyText,
                                    },
                                }}
                                MenuProps={auditionEditSelectMenuProps(selectMenuPaperSx)}>
                                {availableTags.map(tag => (
                                    <MenuItem key={tag} value={tag}>
                                        {tag}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                </Grid>
            </Box>

            <Box sx={{ mb: 3 }}>
                {sectionHeader('Union affiliation')}
                <Grid container spacing={2} sx={{ mt: 1 }}>
                    <Grid item xs={12}>
                        <FormControl fullWidth>
                            <InputLabel id="aud-edit-union-lbl" sx={{ color: outlineLabel }}>
                                Union Affiliation
                            </InputLabel>
                            <Select
                                labelId="aud-edit-union-lbl"
                                id="aud-edit-union-select"
                                value={values.union_affiliation || ''}
                                onChange={e => onFieldChange('union_affiliation', e.target.value)}
                                label="Union Affiliation"
                                sx={{
                                    color: outlineLabel,
                                    backgroundColor: fieldBg,
                                    '& .MuiSelect-select': {
                                        backgroundColor: fieldBg,
                                    },
                                    '& .MuiSvgIcon-root': {
                                        color: bodyText,
                                    },
                                }}
                                MenuProps={auditionEditSelectMenuProps(selectMenuPaperSx)}>
                                <MenuItem value="AEA">AEA</MenuItem>
                                <MenuItem value="SAG-AFTRA">SAG-AFTRA</MenuItem>
                                <MenuItem value="non-union">Non-Union</MenuItem>
                                <MenuItem value="not applicable">Not Applicable</MenuItem>
                                <MenuItem value="ACTRA">ACTRA</MenuItem>
                                <MenuItem value="CAEA">CAEA</MenuItem>
                                <MenuItem value="Equity">Equity</MenuItem>
                                <MenuItem value="MEAA">MEAA</MenuItem>
                                <MenuItem value="non-union affiliation">Non-Union Affiliation</MenuItem>
                                <MenuItem value="other">Other</MenuItem>
                                <MenuItem value="UBCP/ACTRA">UBCP/ACTRA</MenuItem>
                                <MenuItem value="UDA">UDA</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                </Grid>
            </Box>

            <Box sx={{ mb: 3 }}>
                {sectionHeader('Type of work')}
                <Grid container spacing={2} sx={{ mt: 1 }}>
                    <Grid item xs={12}>
                        <FormControl fullWidth>
                            <InputLabel id="aud-edit-tow-lbl" sx={{ color: outlineLabel }}>
                                Type of Work
                            </InputLabel>
                            <Select
                                labelId="aud-edit-tow-lbl"
                                id="aud-edit-tow-select"
                                value={values.type_of_work || ''}
                                onChange={e => onFieldChange('type_of_work', e.target.value)}
                                label="Type of Work"
                                sx={{
                                    color: outlineLabel,
                                    backgroundColor: fieldBg,
                                    '& .MuiSelect-select': {
                                        backgroundColor: fieldBg,
                                    },
                                    '& .MuiSvgIcon-root': {
                                        color: bodyText,
                                    },
                                }}
                                MenuProps={auditionEditSelectMenuProps(selectMenuPaperSx)}>
                                <MenuItem value="ad campaign">Ad Campaign</MenuItem>
                                <MenuItem value="short">Short</MenuItem>
                                <MenuItem value="commercial">Commercial</MenuItem>
                                <MenuItem value="vertical short form">Vertical Short Form</MenuItem>
                                <MenuItem value="episodic">Episodic</MenuItem>
                                <MenuItem value="student film">Student Film</MenuItem>
                                <MenuItem value="theatre">Theatre</MenuItem>
                                <MenuItem value="director's reel">Director's Reel</MenuItem>
                                <MenuItem value="feature">Feature</MenuItem>
                                <MenuItem value="print">Print</MenuItem>
                                <MenuItem value="pilot presentation">Pilot Presentation</MenuItem>
                                <MenuItem value="table read">Table Read</MenuItem>
                                <MenuItem value="live event">Live Event</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                </Grid>
            </Box>

            <Box sx={{ mb: 3 }}>
                {sectionHeader('Usage')}
                <Grid container spacing={2} sx={{ mt: 1 }}>
                    <Grid item xs={12}>
                        <FormControl fullWidth>
                            <InputLabel id="aud-edit-usage-lbl" sx={{ color: outlineLabel }}>
                                Usage
                            </InputLabel>
                            <Select
                                labelId="aud-edit-usage-lbl"
                                id="aud-edit-usage-select"
                                multiple
                                value={values.usage || []}
                                onChange={e => onFieldChange('usage', e.target.value)}
                                label="Usage"
                                renderValue={selected => (
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                        {selected.map((value: any) => (
                                            <Chip
                                                key={value}
                                                label={value}
                                                size="small"
                                                sx={{
                                                    backgroundColor: chipTint,
                                                    color: bodyText,
                                                    fontFamily: FONT_ENUM.MAIN_REGULAR,
                                                    fontSize: '0.75rem',
                                                }}
                                            />
                                        ))}
                                    </Box>
                                )}
                                sx={{
                                    color: outlineLabel,
                                    backgroundColor: fieldBg,
                                    '& .MuiSelect-select': {
                                        backgroundColor: fieldBg,
                                    },
                                    '& .MuiSvgIcon-root': {
                                        color: bodyText,
                                    },
                                }}
                                MenuProps={auditionEditSelectMenuProps(selectMenuPaperSx)}>
                                <MenuItem value="all media">All Media</MenuItem>
                                <MenuItem value="internet">Internet</MenuItem>
                                <MenuItem value="national broadcast">National Broadcast</MenuItem>
                                <MenuItem value="international">International</MenuItem>
                                <MenuItem value="cinema">Cinema</MenuItem>
                                <MenuItem value="personal use">Personal Use</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                </Grid>
            </Box>

            <Box sx={{ mb: 3 }}>
                {sectionHeader('Compensation')}
                <Grid container spacing={2} sx={{ mt: 1 }}>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            select
                            fullWidth
                            label="Compensation"
                            value={values.compensation || 'unpaid'}
                            onChange={e => onFieldChange('compensation', e.target.value)}
                            InputLabelProps={{
                                style: {
                                    color: outlineLabel,
                                },
                            }}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    color: outlineLabel,
                                    backgroundColor: fieldBg,
                                },
                                '& .MuiSelect-select': {
                                    backgroundColor: fieldBg,
                                },
                            }}
                            SelectProps={{
                                id: 'aud-edit-compensation-select',
                                name: 'compensation',
                                MenuProps: auditionEditSelectMenuProps(selectMenuPaperSx),
                                sx: {
                                    '& .MuiSvgIcon-root': {
                                        color: bodyText,
                                    },
                                },
                            }}>
                            <MenuItem value="paid">Paid</MenuItem>
                            <MenuItem value="unpaid">Unpaid</MenuItem>
                            <MenuItem value="tbd">To Be Determined</MenuItem>
                        </TextField>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            select
                            fullWidth
                            label="Employment type"
                            value={values.employment_type || 'one-off'}
                            onChange={e => onFieldChange('employment_type', e.target.value)}
                            InputLabelProps={{
                                style: {
                                    color: outlineLabel,
                                },
                            }}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    color: outlineLabel,
                                    backgroundColor: fieldBg,
                                },
                                '& .MuiSelect-select': {
                                    backgroundColor: fieldBg,
                                },
                            }}
                            SelectProps={{
                                id: 'aud-edit-employment-select',
                                name: 'employment_type',
                                MenuProps: auditionEditSelectMenuProps(selectMenuPaperSx),
                                sx: {
                                    '& .MuiSvgIcon-root': {
                                        color: bodyText,
                                    },
                                },
                            }}>
                            <MenuItem value="contract">Contract</MenuItem>
                            <MenuItem value="full time">Full Time</MenuItem>
                            <MenuItem value="one-off">One-off</MenuItem>
                            <MenuItem value="charity">Charity</MenuItem>
                            <MenuItem value="ongoing">Ongoing</MenuItem>
                        </TextField>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <TextField
                            fullWidth
                            type="number"
                            label="Min Reward"
                            value={values.min_reward || ''}
                            onChange={e => onFieldChange('min_reward', e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <Typography
                                        sx={{
                                            color: outlineLabel,
                                            mr: 1,
                                        }}>
                                        {values.currency || 'USD'}
                                    </Typography>
                                ),
                            }}
                            InputLabelProps={{
                                style: {
                                    color: outlineLabel,
                                },
                            }}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    color: outlineLabel,
                                    backgroundColor: fieldBg,
                                    '& .MuiInputLabel-root': {
                                        color: outlineLabel,
                                    },
                                    '& .MuiOutlinedInput-notchedOutline': {
                                        borderColor: outlineLabel,
                                    },
                                    '&:hover .MuiOutlinedInput-notchedOutline': {
                                        borderColor: outlineLabel,
                                    },
                                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                        borderColor: BRAND_OUTLINE_VARIANT,
                                    },
                                },
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <TextField
                            fullWidth
                            type="number"
                            label="Max Reward"
                            value={values.max_reward || ''}
                            onChange={e => onFieldChange('max_reward', e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <Typography
                                        sx={{
                                            color: outlineLabel,
                                            mr: 1,
                                        }}>
                                        {values.currency || 'USD'}
                                    </Typography>
                                ),
                            }}
                            InputLabelProps={{
                                style: {
                                    color: outlineLabel,
                                },
                            }}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    color: outlineLabel,
                                    backgroundColor: fieldBg,
                                    '& .MuiInputLabel-root': {
                                        color: outlineLabel,
                                    },
                                    '& .MuiOutlinedInput-notchedOutline': {
                                        borderColor: outlineLabel,
                                    },
                                    '&:hover .MuiOutlinedInput-notchedOutline': {
                                        borderColor: outlineLabel,
                                    },
                                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                        borderColor: BRAND_OUTLINE_VARIANT,
                                    },
                                },
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <TextField
                            select
                            fullWidth
                            label="Currency"
                            value={values.currency || 'USD'}
                            onChange={e => onFieldChange('currency', e.target.value)}
                            InputLabelProps={{
                                style: {
                                    color: outlineLabel,
                                },
                            }}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    color: outlineLabel,
                                    backgroundColor: fieldBg,
                                },
                                '& .MuiSelect-select': {
                                    backgroundColor: fieldBg,
                                },
                            }}
                            SelectProps={{
                                id: 'aud-edit-currency-select',
                                name: 'currency',
                                MenuProps: auditionEditSelectMenuProps(selectMenuPaperSx),
                                sx: {
                                    '& .MuiSvgIcon-root': {
                                        color: bodyText,
                                    },
                                },
                            }}>
                            {Object.keys(currencySymbols).map(sym => (
                                <MenuItem key={sym} value={sym}>
                                    {sym} {currencySymbols[sym]}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <TextField
                            select
                            fullWidth
                            label="Reward frequency"
                            value={values.reward_frequency || 'gig'}
                            onChange={e => onFieldChange('reward_frequency', e.target.value)}
                            InputLabelProps={{
                                style: {
                                    color: outlineLabel,
                                },
                            }}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    color: outlineLabel,
                                    backgroundColor: fieldBg,
                                },
                                '& .MuiSelect-select': {
                                    backgroundColor: fieldBg,
                                },
                            }}
                            SelectProps={{
                                id: 'aud-edit-reward-freq-select',
                                name: 'reward_frequency',
                                MenuProps: auditionEditSelectMenuProps(selectMenuPaperSx),
                                sx: {
                                    '& .MuiSvgIcon-root': {
                                        color: bodyText,
                                    },
                                },
                            }}>
                            <MenuItem value="monthly">Monthly</MenuItem>
                            <MenuItem value="weekly">Weekly</MenuItem>
                            <MenuItem value="daily">Daily</MenuItem>
                            <MenuItem value="yearly">Yearly</MenuItem>
                            <MenuItem value="gig">Per Gig</MenuItem>
                        </TextField>
                    </Grid>
                </Grid>
            </Box>

            <Box sx={{ mb: 3 }}>
                {sectionHeader('Location')}
                <Grid container spacing={2} sx={{ mt: 1 }}>
                    <Grid item xs={12}>
                        {isMapsLoaded ? (
                            <PlacesAutocompleteField
                                value={values.shoot_location}
                                label="Shoot Location"
                                placeholder="Search Places ..."
                                onChange={location => onFieldChange('shoot_location', location)}
                                textFieldSx={locationFieldSx}
                                suggestionPalette={locationSuggestionPalette}
                            />
                        ) : (
                            <InfoTextField
                                label="Shoot Location"
                                infoText="The primary location where the audition or shoot will take place"
                                value={values.shoot_location?.address || ''}
                                onChange={value =>
                                    onFieldChange('shoot_location', {
                                        ...values.shoot_location,
                                        address: value,
                                    })
                                }
                            />
                        )}
                    </Grid>
                    <Grid item xs={12}>
                        {isMapsLoaded ? (
                            <PlacesAutocompleteField
                                value={values.work_location}
                                label="Work Location"
                                placeholder="Search Places ..."
                                onChange={location => onFieldChange('work_location', location)}
                                textFieldSx={locationFieldSx}
                                suggestionPalette={locationSuggestionPalette}
                            />
                        ) : (
                            <InfoTextField
                                label="Work Location"
                                infoText="The location where the actual work will be performed"
                                value={locationAddressFromValues(values.work_location)}
                                onChange={value =>
                                    onFieldChange('work_location', mergeLocationAddress(values.work_location, value))
                                }
                            />
                        )}
                    </Grid>
                    <Grid item xs={12}>
                        {isMapsLoaded ? (
                            <PlacesAutocompleteField
                                value={values.talent_location}
                                label="Talent Location"
                                placeholder="Search Places ..."
                                onChange={location => onFieldChange('talent_location', location)}
                                textFieldSx={locationFieldSx}
                                suggestionPalette={locationSuggestionPalette}
                            />
                        ) : (
                            <InfoTextField
                                label="Talent Location"
                                infoText="The location where talent should be based or available"
                                value={locationAddressFromValues(values.talent_location)}
                                onChange={value =>
                                    onFieldChange(
                                        'talent_location',
                                        mergeLocationAddress(values.talent_location, value),
                                    )
                                }
                            />
                        )}
                    </Grid>
                </Grid>
            </Box>

            <Box sx={{ mb: 3 }}>
                {sectionHeader('Dates')}
                <Grid container spacing={2} sx={{ mt: 1 }}>
                    <Grid item xs={12} sm={6}>
                        <CustomDatePicker
                            label="Job Start Date"
                            value={values.job_start_date || ''}
                            onChange={(date, timezone) => {
                                onFieldChange('job_start_date', date);
                                onFieldChange('timezone', timezone);
                            }}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    color: outlineLabel,
                                    backgroundColor: fieldBg,
                                },
                                '& .MuiInputLabel-root': {
                                    color: outlineLabel,
                                },
                            }}
                            brandLuxe={isBrand}
                            withTimezone={false}
                            enableLabel
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <CustomDatePicker
                            label="Job End Date"
                            value={values.job_end_date || ''}
                            onChange={(date, timezone) => {
                                onFieldChange('job_end_date', date);
                                onFieldChange('timezone', timezone);
                            }}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    color: outlineLabel,
                                    backgroundColor: fieldBg,
                                },
                                '& .MuiInputLabel-root': {
                                    color: outlineLabel,
                                },
                            }}
                            brandLuxe={isBrand}
                            withTimezone={false}
                            enableLabel
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <CustomDatePicker
                            label="Rehearsal Date"
                            value={values.rehearsal_date || ''}
                            onChange={(date, timezone) => {
                                onFieldChange('rehearsal_date', date);
                                onFieldChange('timezone', timezone);
                            }}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    color: outlineLabel,
                                    backgroundColor: fieldBg,
                                },
                                '& .MuiInputLabel-root': {
                                    color: outlineLabel,
                                },
                            }}
                            brandLuxe={isBrand}
                            withTimezone={false}
                            enableLabel
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <CustomDatePicker
                            label="Casting Expiration"
                            value={values.casting_expiration || ''}
                            onChange={(date, timezone) => {
                                onFieldChange('casting_expiration', date);
                                if (timezone) {
                                    onFieldChange('timezone', timezone);
                                }
                            }}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    color: outlineLabel,
                                    backgroundColor: fieldBg,
                                },
                                '& .MuiInputLabel-root': {
                                    color: outlineLabel,
                                },
                            }}
                            brandLuxe={isBrand}
                            withTimezone={false}
                            enableLabel
                        />
                    </Grid>
                </Grid>
            </Box>

            <Box sx={{ mb: 3 }}>
                {sectionHeader('Requirements')}
                <Grid container spacing={2} sx={{ mt: 1 }}>
                    <Grid item xs={12}>
                        <FormControl fullWidth>
                            <InputLabel id="aud-edit-reqmedia-lbl" sx={{ color: outlineLabel }}>
                                Required Media
                            </InputLabel>
                            <Select
                                labelId="aud-edit-reqmedia-lbl"
                                id="aud-edit-reqmedia-select"
                                multiple
                                value={values.required_media || []}
                                onChange={e => onFieldChange('required_media', e.target.value)}
                                label="Required Media"
                                sx={{
                                    color: outlineLabel,
                                    backgroundColor: fieldBg,
                                    '& .MuiSelect-select': {
                                        backgroundColor: fieldBg,
                                    },
                                    '& .MuiSvgIcon-root': {
                                        color: bodyText,
                                    },
                                }}
                                MenuProps={auditionEditSelectMenuProps(selectMenuPaperSx)}>
                                <MenuItem value="head shot">Head Shot</MenuItem>
                                <MenuItem value="audio reel">Audio Reel</MenuItem>
                                <MenuItem value="demo reel">Demo Reel</MenuItem>
                                <MenuItem value="fullbody shot">Full Body Shot</MenuItem>
                                <MenuItem value="portfolio">Portfolio</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12}>
                        <FormControl fullWidth>
                            <InputLabel id="aud-edit-reqskill-lbl" sx={{ color: outlineLabel }}>
                                Required Skills
                            </InputLabel>
                            <Select
                                labelId="aud-edit-reqskill-lbl"
                                id="aud-edit-reqskill-select"
                                multiple
                                value={values.required_skill || []}
                                onChange={e => onFieldChange('required_skill', e.target.value)}
                                label="Required Skills"
                                sx={{
                                    color: outlineLabel,
                                    backgroundColor: fieldBg,
                                    '& .MuiSelect-select': {
                                        backgroundColor: fieldBg,
                                    },
                                    '& .MuiSvgIcon-root': {
                                        color: bodyText,
                                    },
                                }}
                                MenuProps={auditionEditSelectMenuProps(selectMenuPaperSx)}>
                                <MenuItem value="actor">Actor</MenuItem>
                                <MenuItem value="actress">Actress</MenuItem>
                                <MenuItem value="extra">Extra</MenuItem>
                                <MenuItem value="model">Model</MenuItem>
                                <MenuItem value="voice artist">Voice Artist</MenuItem>
                                <MenuItem value="technical">Technical</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            type="number"
                            label="Minimum Age"
                            value={values.min_age || ''}
                            onChange={e => onFieldChange('min_age', e.target.value)}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    color: outlineLabel,
                                    backgroundColor: fieldBg,
                                },
                            }}
                            InputLabelProps={{
                                style: {
                                    color: outlineLabel,
                                },
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            type="number"
                            label="Maximum Age"
                            value={values.max_age || ''}
                            onChange={e => onFieldChange('max_age', e.target.value)}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    color: outlineLabel,
                                    backgroundColor: fieldBg,
                                },
                            }}
                            InputLabelProps={{
                                style: {
                                    color: outlineLabel,
                                },
                            }}
                        />
                    </Grid>
                </Grid>
            </Box>

            <Box sx={{ mb: 3 }}>
                {sectionHeader('Submission report settings')}
                <Grid container spacing={2} sx={{ mt: 1 }}>
                    <Grid item xs={12}>
                        <TextField
                            select
                            fullWidth
                            label="Submission report"
                            value={values.submission_report || 'daily digest'}
                            onChange={e => onFieldChange('submission_report', e.target.value)}
                            InputLabelProps={{
                                style: {
                                    color: outlineLabel,
                                },
                            }}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    color: outlineLabel,
                                    backgroundColor: fieldBg,
                                },
                                '& .MuiSelect-select': {
                                    backgroundColor: fieldBg,
                                },
                            }}
                            SelectProps={{
                                id: 'aud-edit-submission-report-select',
                                name: 'submission_report',
                                MenuProps: auditionEditSelectMenuProps(selectMenuPaperSx),
                                sx: {
                                    '& .MuiSvgIcon-root': {
                                        color: bodyText,
                                    },
                                },
                            }}>
                            <MenuItem value="daily digest">Daily Digest</MenuItem>
                            <MenuItem value="weekly digest">Weekly Digest</MenuItem>
                            <MenuItem value="monthly digest">Monthly Digest</MenuItem>
                            <MenuItem value="applications as they come in">Applications as they come in</MenuItem>
                            <MenuItem value="prerequisite">Prerequisite</MenuItem>
                        </TextField>
                    </Grid>
                </Grid>
            </Box>

            <Box sx={{ mb: 3 }}>
                {sectionHeader('Attachments')}
                <Grid container spacing={2} sx={{ mt: 1 }}>
                    <Grid item xs={12}>
                        <Box
                            sx={{
                                p: 2,
                                borderRadius: 1,
                            }}>
                            <Grid container spacing={2}>
                                <Grid item xs={12}>
                                    <TextField
                                        select
                                        fullWidth
                                        label="Attachment Type"
                                        value={values.attachment_type || ''}
                                        onChange={e => onFieldChange('attachment_type', e.target.value)}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                color: outlineLabel,
                                                backgroundColor: fieldBg,
                                            },
                                        }}
                                        InputLabelProps={{
                                            style: {
                                                color: outlineLabel,
                                            },
                                        }}
                                        SelectProps={{
                                            id: 'aud-edit-attachment-type-select',
                                            name: 'attachment_type',
                                            MenuProps: auditionEditSelectMenuProps(selectMenuPaperSx),
                                            sx: {
                                                '& .MuiSvgIcon-root': {
                                                    color: bodyText,
                                                },
                                            },
                                        }}>
                                        <MenuItem value="side">Side</MenuItem>
                                        <MenuItem value="details">Details</MenuItem>
                                        <MenuItem value="others">Others</MenuItem>
                                        <MenuItem value="pre-requisite">Pre-requisite</MenuItem>
                                        <MenuItem value="requirement">Requirement</MenuItem>
                                    </TextField>
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        multiline
                                        rows={2}
                                        label="Description"
                                        InputLabelProps={{
                                            style: {
                                                color: outlineLabel,
                                            },
                                        }}
                                        value={values.attachment_description || ''}
                                        onChange={e => onFieldChange('attachment_description', e.target.value)}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                color: outlineLabel,
                                                backgroundColor: fieldBg,
                                            },
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    {values.attachment ? (
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 2,
                                                p: 2,
                                                border: `1px solid ${BRAND_OUTLINE_VARIANT}`,
                                                borderRadius: 1,
                                            }}>
                                            <PictureAsPdf
                                                sx={{
                                                    fontSize: 40,
                                                    color: accent,
                                                }}
                                            />
                                            <Typography
                                                sx={{
                                                    flex: 1,
                                                    fontFamily: FONT_ENUM.MAIN_REGULAR,
                                                    color: bodyText,
                                                }}>
                                                Current Attachment
                                            </Typography>
                                            <Button
                                                variant="outlined"
                                                startIcon={<Delete />}
                                                onClick={() => onFieldChange('attachment', '')}
                                                sx={{
                                                    borderColor: BRAND_OUTLINE_VARIANT,
                                                    color: bodyText,
                                                    '&:hover': {
                                                        borderColor: BRAND_OUTLINE_VARIANT,
                                                        backgroundColor: 'rgba(255,255,255,0.05)',
                                                    },
                                                }}>
                                                Remove
                                            </Button>
                                        </Box>
                                    ) : (
                                        <>
                                            <input
                                                type="file"
                                                id="file-upload"
                                                style={{ display: 'none' }}
                                                onChange={e => {
                                                    const file = e.target.files?.[0];
                                                    if (file) {
                                                        onFieldChange('attachment', file.name);
                                                    }
                                                }}
                                            />
                                            <Button
                                                variant="outlined"
                                                component="label"
                                                htmlFor="file-upload"
                                                startIcon={<UploadFile />}
                                                sx={{
                                                    borderColor: BRAND_OUTLINE_VARIANT,
                                                    color: bodyText,
                                                    '&:hover': {
                                                        borderColor: BRAND_OUTLINE_VARIANT,
                                                        backgroundColor: 'rgba(255,255,255,0.05)',
                                                    },
                                                }}>
                                                Upload File
                                            </Button>
                                        </>
                                    )}
                                </Grid>
                            </Grid>
                        </Box>
                    </Grid>
                </Grid>
            </Box>
        </Box>
    );
};
